import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import '../input.css'
import scrollToHeroEmbed from '../utils/scrollToHeroEmbed'
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined'
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined'
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import Seo from './Seo'
import BookDemoCta, { BookDemoPrimaryButton } from './BookDemoCta'

const WorkWithUsPage = () => {
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const items = document.querySelectorAll('.reveal-on-scroll')
    if (!items.length || typeof IntersectionObserver === 'undefined') return
    items.forEach((item) => item.classList.remove('is-visible'))
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.2 }
    )
    items.forEach((item) => observer.observe(item))
    return () => observer.disconnect()
  }, [i18n.language])

  const values = t('work_with_us.values', { returnObjects: true }) || []
  const avoid = t('work_with_us.avoid', { returnObjects: true }) || []
  const thrive = t('work_with_us.thrive', { returnObjects: true }) || []
  const notFor = t('work_with_us.not_for', { returnObjects: true }) || []
  const reachBody = t('work_with_us.section_reach_body')
  const reachEmail = 'careers@leanit101.com'
  const [reachBefore, reachAfter = ''] = String(reachBody).split(reachEmail)
  const iconToneCycle = [
    'bg-pyramid-1/15 text-pyramid-1',
    'bg-pyramid-10/15 text-pyramid-10',
    'bg-pyramid-19/15 text-pyramid-19',
    'bg-pyramid-5/15 text-pyramid-5',
    'bg-pyramid-15/15 text-pyramid-15'
  ]
  const iconSet = [
    <TrackChangesOutlinedIcon fontSize="inherit" />,
    <PsychologyOutlinedIcon fontSize="inherit" />,
    <BoltOutlinedIcon fontSize="inherit" />,
    <GppGoodOutlinedIcon fontSize="inherit" />,
    <BalanceOutlinedIcon fontSize="inherit" />,
    <PrecisionManufacturingOutlinedIcon fontSize="inherit" />,
    <EngineeringOutlinedIcon fontSize="inherit" />,
    <QueryStatsOutlinedIcon fontSize="inherit" />
  ]

  const renderList = (items) => (
    Array.isArray(items) && items.map((item, index) => (
      <li key={item} className="work-item flex items-center gap-3">
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${iconToneCycle[index % iconToneCycle.length]} text-[17px]`}
        >
          {iconSet[index % iconSet.length]}
        </span>
        <span>{item}</span>
      </li>
    ))
  )

  return (
    <div className="bg-vulcan text-white min-h-screen pt-28 pb-20 px-6 md:px-16">
      <Seo
        title={t('seo.work_with_us_title')}
        description={t('seo.work_with_us_description')}
        path="/work-with-us"
        image={t('seo.work_with_us_image')}
      />
      <div className="max-w-5xl mx-auto">
        <header className="mb-12 reveal-on-scroll" style={{ '--reveal-delay': '40ms' }}>
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">
            {t('work_with_us.subtitle')}
          </p>
          <h1 className="mt-4 text-4xl md:text-5xl font-light tracking-[0.08em] uppercase text-pyramid-1">
            {t('work_with_us.title')}
          </h1>
          <div className="mt-6">
            <BookDemoPrimaryButton />
          </div>
        </header>

        <div className="grid gap-8">
          <section className="reveal-on-scroll rounded-2xl border border-white/10 bg-gradient-to-br from-white/8 via-white/4 to-transparent p-6 card-hover" style={{ '--reveal-delay': '120ms' }}>
            <div>
              <h3 className="text-xl font-semibold text-white">
                {t('work_with_us.section_company_title')}
              </h3>
              <p className="mt-3 text-slate-300">
                {t('work_with_us.section_company_body')}
              </p>
            </div>
          </section>

          <section className="reveal-on-scroll rounded-2xl border border-white/10 bg-gradient-to-br from-white/8 via-white/4 to-transparent p-6 card-hover" style={{ '--reveal-delay': '200ms' }}>
            <div className="w-full">
              <h3 className="text-xl font-semibold text-white">
                {t('work_with_us.section_how_title')}
              </h3>
              <div className="mt-4 grid gap-6 text-slate-300 md:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 card-hover">
                  <div className="uppercase text-xs tracking-[0.14em] text-white/70">
                    {t('work_with_us.values_title')}
                  </div>
                  <ul className="mt-3 space-y-2">
                    {renderList(values)}
                  </ul>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 card-hover">
                  <div className="uppercase text-xs tracking-[0.14em] text-white/70">
                    {t('work_with_us.avoid_title')}
                  </div>
                  <ul className="mt-3 space-y-2">
                    {renderList(avoid)}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="reveal-on-scroll rounded-2xl border border-white/10 bg-gradient-to-br from-white/8 via-white/4 to-transparent p-6 card-hover" style={{ '--reveal-delay': '280ms' }}>
            <div className="w-full">
              <h3 className="text-xl font-semibold text-white">
                {t('work_with_us.section_thrive_title')}
              </h3>
              <div className="mt-4 grid gap-6 text-slate-300 md:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 card-hover">
                  <div className="uppercase text-xs tracking-[0.14em] text-white/70">
                    {t('work_with_us.thrive_title')}
                  </div>
                  <ul className="mt-3 space-y-2">
                    {renderList(thrive)}
                  </ul>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 card-hover">
                  <div className="uppercase text-xs tracking-[0.14em] text-white/70">
                    {t('work_with_us.not_for_title')}
                  </div>
                  <ul className="mt-3 space-y-2">
                    {renderList(notFor)}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="reveal-on-scroll rounded-2xl border border-white/10 bg-gradient-to-br from-white/8 via-white/4 to-transparent p-6 card-hover" style={{ '--reveal-delay': '360ms' }}>
            <div>
              <h3 className="text-xl font-semibold text-white">
                {t('work_with_us.section_reach_title')}
              </h3>
              <p className="mt-3 text-slate-300">
                {reachBefore}
                <a
                  href={`mailto:${reachEmail}`}
                  className="text-pyramid-19 hover:text-pyramid-10 underline underline-offset-2 transition-colors"
                >
                  {reachEmail}
                </a>
                {reachAfter}
              </p>
            </div>
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 reveal-on-scroll" style={{ '--reveal-delay': '520ms' }}>
          <button
            type="button"
            onClick={scrollToHeroEmbed}
            className="rounded-[8px] border border-white/40 px-6 py-3 text-white/90"
          >
            {t('work_with_us.cta_talk')}
          </button>
          <a
            href="mailto:contact@leanit101.com"
            className="rounded-[8px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-6 py-3 font-semibold text-ebony"
          >
            {t('work_with_us.cta_contact')}
          </a>
        </div>
        <div className="mt-12">
          <BookDemoCta />
        </div>
      </div>
    </div>
  )
}

export default WorkWithUsPage
